import { motion } from 'framer-motion';
import {
    Calendar as CalendarIcon,
    ChevronLeft,
    ChevronRight,
    Clock,
    Plus,
    Search,
} from 'lucide-react';
import React, { useState, useEffect, useCallback } from 'react';
import { calendarAPI, employeeAPI } from '../../utils/api';
import CalendarEventModal from '../../components/calendar/CalendarEventModal';
import CalendarGrid from '../../components/calendar/CalendarGrid';
import DayEventsModal from '../../components/calendar/DayEventsModal';
import EventCard from '../../components/calendar/EventCard';
import EmployeeSelector from '../../components/calendar/EmployeeSelector';
import toast from 'react-hot-toast';

// Helper function to format employee data for calendar
const formatEmployeeForCalendar = (employee) => ({
  id: employee._id || employee.id,
  name: `${employee.firstName} ${employee.lastName}`,
  avatar: employee.firstName?.charAt(0) || 'U',
  position: employee.position || 'Employee',
  department: employee.department || ''
});

// Helper function to format event data for calendar
const formatEventForCalendar = (event) => ({
  id: event._id || event.id,
  title: event.title,
  description: event.description,
  date: event.date,
  time: event.time,
  endTime: event.endTime,
  type: event.type,
  taggedEmployees: event.taggedEmployees?.map(emp => emp._id || emp.id) || [],
  location: event.location,
  color: event.color,
  status: event.status,
  priority: event.priority,
  isAllDay: event.isAllDay,
  createdBy: event.createdBy,
  createdAt: event.createdAt,
  updatedAt: event.updatedAt
});

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: "spring", stiffness: 300, damping: 24 }
  }
};

const Calendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month'); // month, week, day
  const [showEventModal, setShowEventModal] = useState(false);
  const [showDayEventsModal, setShowDayEventsModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [events, setEvents] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filterEmployee, setFilterEmployee] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [eventsLoading, setEventsLoading] = useState(false);

  // Load employees from API
  const loadEmployees = useCallback(async () => {
    try {
      const response = await employeeAPI.getEmployees();
      const employeesData = response.data?.data?.employees || response.data?.employees || [];
      const formattedEmployees = employeesData.map(formatEmployeeForCalendar);
      setEmployees(formattedEmployees);
    } catch (error) {
      console.error('Error loading employees:', error);
      toast.error('Failed to load employees');
    }
  }, []);

  // Load events from API
  const loadEvents = useCallback(async () => {
    try {
      setEventsLoading(true);
      
      // Calculate date range based on current view
      let startDate, endDate;
      const current = new Date(currentDate);
      
      if (view === 'month') {
        startDate = new Date(current.getFullYear(), current.getMonth(), 1);
        endDate = new Date(current.getFullYear(), current.getMonth() + 1, 0);
      } else if (view === 'week') {
        startDate = new Date(current);
        startDate.setDate(current.getDate() - current.getDay());
        endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);
      } else { // day view
        startDate = new Date(current);
        endDate = new Date(current);
      }

      const params = {
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        ...(filterEmployee && { employeeId: filterEmployee }),
        ...(searchQuery && { search: searchQuery })
      };

      const response = await calendarAPI.getEvents(params);
      const eventsData = response.data?.data?.events || response.data?.events || [];
      const formattedEvents = eventsData.map(formatEventForCalendar);
      setEvents(formattedEvents);
    } catch (error) {
      console.error('Error loading events:', error);
      toast.error('Failed to load calendar events');
    } finally {
      setEventsLoading(false);
    }
  }, [currentDate, view, filterEmployee, searchQuery]);

  // Load employees and events on component mount
  useEffect(() => {
    const initializeData = async () => {
      await loadEmployees();
      await loadEvents();
    };
    initializeData();
  }, [loadEmployees, loadEvents]);

  // Load events when date range changes
  useEffect(() => {
    if (employees.length > 0) {
      loadEvents();
    }
  }, [loadEvents, employees.length]);

  const handlePrev = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (view === 'month') {
        newDate.setMonth(prev.getMonth() - 1);
      } else if (view === 'week') {
        newDate.setDate(prev.getDate() - 7);
      } else {
        newDate.setDate(prev.getDate() - 1);
      }
      return newDate;
    });
  };

  const handleNext = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (view === 'month') {
        newDate.setMonth(prev.getMonth() + 1);
      } else if (view === 'week') {
        newDate.setDate(prev.getDate() + 7);
      } else {
        newDate.setDate(prev.getDate() + 1);
      }
      return newDate;
    });
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleDateClick = (date) => {
    // Show all events for this date (view mode)
    setSelectedDate(date);
    setSelectedEvent(null);
    setShowDayEventsModal(true);
  };

  const handleCreateEvent = (date) => {
    // Create new event for this specific date
    setSelectedDate(date);
    setSelectedEvent(null);
    setShowEventModal(true);
  };

  const handleEditEvent = (event) => {
    // Edit existing event
    setSelectedEvent(event);
    setSelectedDate(event.date);
    setShowDayEventsModal(false);
    setShowEventModal(true);
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setSelectedDate(event.date);
    setShowEventModal(true);
  };

  const handleSaveEvent = async (eventData) => {
    try {
      if (selectedEvent) {
        // Update existing event
        const response = await calendarAPI.updateEvent(selectedEvent.id, eventData);
        const updatedEvent = formatEventForCalendar(response.data.data.event);
        setEvents(prev => prev.map(e => e.id === selectedEvent.id ? updatedEvent : e));
        toast.success('Event updated successfully');
      } else {
        // Create new event
        const response = await calendarAPI.createEvent(eventData);
        const newEvent = formatEventForCalendar(response.data.data.event);
        setEvents(prev => [...prev, newEvent]);
        toast.success('Event created successfully');
      }
      setShowEventModal(false);
      setSelectedEvent(null);
      setSelectedDate(null);
    } catch (error) {
      console.error('Error saving event:', error);
      const errorMessage = error.response?.data?.message || 'Failed to save event';
      toast.error(errorMessage);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await calendarAPI.deleteEvent(eventId);
      setEvents(prev => prev.filter(e => e.id !== eventId));
      setShowEventModal(false);
      setSelectedEvent(null);
      toast.success('Event deleted successfully');
    } catch (error) {
      console.error('Error deleting event:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete event';
      toast.error(errorMessage);
    }
  };

  const filteredEvents = events.filter(event => {
    const matchesEmployee = !filterEmployee || event.taggedEmployees.includes(filterEmployee);
    const matchesSearch = !searchQuery ||
      event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesEmployee && matchesSearch;
  });

  const getEventsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return events.filter(event => event.date === dateStr);
  };

  const formatDateRange = () => {
    if (view === 'month') {
      return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    } else if (view === 'week') {
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      return `${startOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } else {
      return currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    }
  };



  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header */}
      <motion.div
        className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8"
        variants={itemVariants}
      >
        <div>
          <h1 className="page-title">Calendar</h1>
          <p className="text-gray-600 mt-1">Manage your schedule, events, and team activities</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 mt-4 lg:mt-0">
          {/* Search */}
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search events..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input-field pl-10 w-64"
            />
          </div>

          {/* Employee Filter */}
          <EmployeeSelector
            employees={employees}
            selectedEmployee={filterEmployee}
            onSelect={setFilterEmployee}
            placeholder="Filter by employee"
            className="w-48"
          />

          {/* Create Event Button */}
          <motion.button
            className="btn-primary flex items-center"
            onClick={() => {
              setSelectedEvent(null);
              setSelectedDate(currentDate.toISOString().split('T')[0]);
              setShowEventModal(true);
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Plus size={16} className="mr-2" />
            Create Event
          </motion.button>
        </div>
      </motion.div>

      {/* Calendar Controls */}
      <motion.div
        className="card card-hover mb-6"
        variants={itemVariants}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          {/* Navigation */}
          <div className="flex items-center gap-4">
            <motion.button
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              onClick={handlePrev}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <ChevronLeft size={20} />
            </motion.button>

            <h2 className="subsection-title">{formatDateRange()}</h2>

            <motion.button
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              onClick={handleNext}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <ChevronRight size={20} />
            </motion.button>

            <motion.button
              className="btn-secondary"
              onClick={handleToday}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Today
            </motion.button>
          </div>

          {/* View Toggle */}
          <div className="flex items-center gap-2">
            {['month', 'week', 'day'].map((viewType) => (
              <motion.button
                key={viewType}
                className={`px-4 py-2 text-sm font-medium rounded-lg capitalize transition-colors ${
                  view === viewType
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                onClick={() => setView(viewType)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {viewType}
              </motion.button>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Calendar Grid */}
      <motion.div
        className="card card-hover mb-6"
        variants={itemVariants}
      >
        {eventsLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <span className="ml-3 text-gray-600">Loading calendar events...</span>
          </div>
        ) : (
          <CalendarGrid
            currentDate={currentDate}
            view={view}
            events={filteredEvents}
            employees={employees}
            onDateClick={handleDateClick}
            onEventClick={handleEventClick}
            onCreateEvent={handleCreateEvent}
          />
        )}
      </motion.div>

      {/* Upcoming Events */}
      <motion.div
        className="grid grid-cols-1 lg:grid-cols-2 gap-6"
        variants={itemVariants}
      >
        {/* Today's Events */}
        <div className="card card-hover">
          <div className="flex items-center justify-between mb-6">
            <h2 className="subsection-title">Today's Events</h2>
            <div className="flex items-center text-sm text-gray-500">
              <Clock size={16} className="mr-1" />
              {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </div>
          </div>

          <div className="space-y-3">
            {filteredEvents
              .filter(event => event.date === new Date().toISOString().split('T')[0])
              .sort((a, b) => a.time.localeCompare(b.time))
              .map(event => (
                <EventCard
                  key={event.id}
                  event={event}
                  employees={employees}
                  onClick={() => handleEventClick(event)}
                />
              ))}
            {filteredEvents.filter(event => event.date === new Date().toISOString().split('T')[0]).length === 0 && (
              <p className="text-gray-500 text-sm">No events scheduled for today</p>
            )}
          </div>
        </div>

        {/* Upcoming Events */}
        <div className="card card-hover">
          <div className="flex items-center justify-between mb-6">
            <h2 className="subsection-title">Upcoming Events</h2>
            <div className="flex items-center text-sm text-gray-500">
              <CalendarIcon size={16} className="mr-1" />
              Next 7 days
            </div>
          </div>

          <div className="space-y-3 max-h-80 overflow-y-auto">
            {filteredEvents
              .filter(event => {
                const eventDate = new Date(event.date);
                const today = new Date();
                const weekFromNow = new Date();
                weekFromNow.setDate(today.getDate() + 7);
                return eventDate >= today && eventDate <= weekFromNow;
              })
              .sort((a, b) => new Date(a.date + ' ' + a.time) - new Date(b.date + ' ' + b.time))
              .slice(0, 5)
              .map(event => (
                <EventCard
                  key={event.id}
                  event={event}
                  employees={employees}
                  onClick={() => handleEventClick(event)}
                />
              ))}
            {filteredEvents.filter(event => {
              const eventDate = new Date(event.date);
              const today = new Date();
              const weekFromNow = new Date();
              weekFromNow.setDate(today.getDate() + 7);
              return eventDate >= today && eventDate <= weekFromNow;
            }).length === 0 && (
              <p className="text-gray-500 text-sm">No upcoming events in the next 7 days</p>
            )}
          </div>
        </div>
      </motion.div>

      {/* Event Modal */}
      {showEventModal && (
        <CalendarEventModal
          isOpen={showEventModal}
          onClose={() => {
            setShowEventModal(false);
            setSelectedEvent(null);
            setSelectedDate(null);
          }}
          event={selectedEvent}
          date={selectedDate}
          employees={employees}
          onSave={handleSaveEvent}
          onDelete={handleDeleteEvent}
        />
      )}

      {/* Day Events Modal */}
      {showDayEventsModal && (
        <DayEventsModal
          isOpen={showDayEventsModal}
          onClose={() => {
            setShowDayEventsModal(false);
            setSelectedDate(null);
          }}
          date={selectedDate}
          events={selectedDate ? getEventsForDate(new Date(selectedDate)) : []}
          employees={employees}
          onEditEvent={handleEditEvent}
          onCreateEvent={handleCreateEvent}
        />
      )}
    </motion.div>
  );
};

export default Calendar;
